import { Dispatch, SetStateAction, useState, useMemo } from 'react';
import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { CustomPicker } from '@components/CustomPickerWithBorder/CustomPicker';
import { combineTags } from '@services/queries/tags/combineTags/combineTags';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { indexTagsCategory } from '@services/queries/tagsCategory';
import { Controller, useForm } from 'react-hook-form';
import { Tag as TagComponent } from '@components/Tag/Tag';
import { ITag } from '@models/Tag.model';
import { CategoryColorBadge } from '@components/CategoryColorBadge/CategoryColorBadge';
import { trackCombineTags } from '@services/mixpanel/trackers/TagsMetrics';
import { groupSelectedTags } from '@components/Filters/Filter/components/FilterContent/TagsFilter/utils';
import { CountLoading } from '../loading/Tags.loading';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Confirm,
  Line,
  Form,
  Divider,
  Label,
  Count,
  Input,
  Left,
  Right,
  HeaderWrapper,
  Wrapper,
  TagsWrapper,
} from './CombineDialog.styles';

const CountComponent = ({ count }: { count?: number }) => {
  if (count !== undefined) {
    return <Count>{count}</Count>;
  }

  return <CountLoading />;
};

interface ICombineDialog {
  open: boolean;
  onClose: () => void;
  tagsIds: string[];
  setChecked: Dispatch<SetStateAction<string[]>>;
  setSelectedTags: Dispatch<SetStateAction<ITag[]>>;
  selectedTags: ITag[];
}

interface IFormData {
  tagsIds: string[];
  tagCategoryId: string;
  name: string;
}

export const CombineDialog = ({
  open,
  onClose,
  tagsIds,
  setChecked,
  selectedTags,
  setSelectedTags,
}: ICombineDialog) => {
  const portalDiv = document.getElementById('portal');

  const queryClient = useQueryClient();

  const { data } = useQuery(['tagsCategory'], () => indexTagsCategory());

  const Options = data?.map(option => {
    return {
      value: option.id,
      label: <CategoryColorBadge color={option.color} name={option.name} />,
    };
  });

  const newData = data ?? [];

  const schema = yup.object().shape({
    name: yup.string().required(),
    tagCategoryId: yup.string().required(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { isValid },
    control,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const categoriesCount = newData
    .map(tagCategories => {
      const validTags = selectedTags.filter(
        tag => tag.tagCategoryId === tagCategories.id,
      );
      if (validTags.length === 0) return undefined;
      const tagGroup = { tagCategories, validTags };
      return tagGroup;
    })
    .filter(tagGroup => !!tagGroup);

  const affectedCategoriesNames = categoriesCount.map(e =>
    e ? e.tagCategories.name : '',
  );

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const category = data?.find(e => e.id === selectedCategoryId);
  const filteredCategory = category ? category.name : '';

  const mutationCombineTags = useMutation(combineTags, {
    onSuccess: () => {
      queryClient.invalidateQueries('tags');
      queryClient.invalidateQueries('tagCategories');
      trackCombineTags(
        tagsIds.length,
        categoriesCount.length,
        affectedCategoriesNames,
        filteredCategory,
      );
      onClose();
      setValue('name', '');
      setValue('tagCategoryId', '');
      setChecked([]);
      setSelectedTags([]);
    },
  });

  const onSubmit = (TagData: IFormData) => {
    if (!mutationCombineTags.isLoading) {
      if (data)
        mutationCombineTags.mutate({
          name: TagData.name,
          tagCategoryId: TagData.tagCategoryId,
          tagsIds,
        });
    }
  };
  // Group the tags
  const selectedMap = useMemo(() => {
    return groupSelectedTags(selectedTags);
  }, [selectedTags]);

  // Get the grouped categories.
  const selectedCategories = newData.filter(e => {
    return selectedMap.has(e.id);
  });

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Left>
              <HeaderWrapper>
                <Title>Combine Tags</Title>
                <CountComponent count={tagsIds.length} />
              </HeaderWrapper>

              <Divider height="24px" />

              <Label>
                You’re about to combine the following tags into a new tag:
              </Label>

              <Divider height="24px" />

              <Wrapper>
                {selectedCategories.map(item => {
                  const thisCategory = selectedMap.get(item.id);
                  if (!thisCategory) return null;
                  const validTags = Array.from(thisCategory.values(), tag => {
                    return (
                      <TagComponent
                        id={tag.id}
                        tagName={tag.name}
                        tagCategory={item.name}
                        color={item.color}
                      />
                    );
                  });
                  return (
                    <div>
                      <Label className="optional">{item.name}</Label>
                      <TagsWrapper>{validTags}</TagsWrapper>
                    </div>
                  );
                })}
              </Wrapper>
            </Left>
            <Right>
              <Header>
                <Title>New Tag</Title>
                <Close
                  onClick={() => {
                    onClose();
                    setValue('name', '');
                    setValue('tagCategoryId', '');
                  }}
                >
                  <BlueIcon icon="cross" color="#8F95B2" />
                </Close>
              </Header>
              <Divider height="28px" />

              <Label>Type a tag name and category for the new tag below:</Label>

              <Form onSubmit={handleSubmit(onSubmit)}>
                <Label className="mandatory">New tag name</Label>

                <Input placeholder="Ex. Studio" {...register('name')} />

                <Label className="mandatory">Move to</Label>

                <Controller
                  control={control}
                  name="tagCategoryId"
                  render={({ field: { onChange, value } }) => (
                    <CustomPicker
                      options={Options}
                      placeholder="Select a Category"
                      value={Options?.find(c => c.value === value)}
                      onChange={(val: any) => {
                        onChange(val.value);
                        setSelectedCategoryId(val.value);
                      }}
                    />
                  )}
                />

                <Line>
                  <Confirm
                    type="submit"
                    disabled={!isValid || mutationCombineTags.isLoading}
                  >
                    Combine
                  </Confirm>
                </Line>
              </Form>
            </Right>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
